import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  IconButton,
  Slide,
  Typography,
  TextField,
  Fade,
  CircularProgress
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SentimentVeryDissatisfiedIcon from '@material-ui/icons/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@material-ui/icons/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@material-ui/icons/SentimentSatisfied';
import SentimentSatisfiedAltIcon from '@material-ui/icons/SentimentSatisfiedAltOutlined';
import SentimentVerySatisfiedIcon from '@material-ui/icons/SentimentVerySatisfied';
import Rating from '@material-ui/lab/Rating';
import API from '../api/api.js';
import { useSelector, useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

const customIcons = {
  1: {
    icon: <SentimentVeryDissatisfiedIcon fontSize="large" />, label: 'Very Dissatisfied',
  },
  2: {
    icon: <SentimentDissatisfiedIcon fontSize="large" />, label: 'Dissatisfied',
  },
  3: {
    icon: <SentimentSatisfiedIcon fontSize="large" />, label: 'Neutral',
  },
  4: {
    icon: <SentimentSatisfiedAltIcon fontSize="large" />, label: 'Satisfied',
  },
  5: {
    icon: <SentimentVerySatisfiedIcon fontSize="large" />, label: 'Very Satisfied',
  },
};

const useStyles = makeStyles({
  root: {
    width: 450,
    maxWidth: 450,
    position: 'fixed',
    bottom: 65,
    right: 24,
    overflow: 'hidden',
    zIndex: 100,
    textAlign: 'center',
  },
  ratingContainer: {
    textAlign: 'center',
    margin: '.5rem 0',
    '& svg': {
      width: '2.5rem',
      height: '2.5rem',
      margin: 4,
    },
  },
  closeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    cursor: 'pointer',
  },
  backBtn: {
    position: 'absolute',
    top: 16,
    left: 16,
    cursor: 'pointer',
    color: 'lightgray',
  },
  cardContent: {
    width: '100%',
  },
  cardActions: {
    justifyContent: 'end',
  },
  hiddenContainer: {
    position: 'relative',
    width: '100%',
    top: 0,
    left: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  feedbackForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '1rem',
    width: '100%',
  },
  innerCardContent: {
    padding: 16,
    marginTop: 24,
  },
  checkIcon: {
    fontSize: 80,
    color: 'green',
  },
});

function IconContainer(props) {
  const { value, ...other } = props;
  return <span {...other}>{customIcons[value].icon}</span>;
}

IconContainer.propTypes = {
  value: PropTypes.number.isRequired,
};

export default function SatisfactionQuote() {
  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [feedback, setFeedback] = useState('');

  const currentPlan = useSelector((state) => state.shop.shop.charge);

  useEffect(() => {
    const isClosed = window.localStorage.getItem('ly-satisfaction-quote-dismissed');
    if (!isClosed) {
      setTimeout(() => {
        if (isBetween7And30Days(currentPlan.activated_on)) {
          setOpen(true);
        }
      }, 3000);
    }
  }, []);

  const handleClose = () => {
    window.localStorage.setItem('ly-satisfaction-quote-dismissed', true);
    setOpen(false);
  };

  const handleRatingChange = (event, value) => {
    setRate(value);
    if (value >= 4) {
      setTimeout(() => setShowThankYou(true), 300);
    } else {
      setTimeout(() => setShowForm(true), 300);
    }
  };

  const handleBack = () => {
    setShowThankYou(false);
    setShowForm(false);
    setRate(null);
  };

  const writeReview = () => {
    handleClose();
    window.open('https://apps.shopify.com/langify/reviews', '_blank');
  }

  const handleSubmit = () => {
    setSendingFeedback(true);
    let feedbackString = `
    ${feedback}

    ---------------
    
    ${rate} Star${rate > 1 && 's'} quoted.
    `;
    API.sendAppFeedback(feedbackString, () => {
      setSendingFeedback(false);
      setShowForm(false);
      setShowCheck(true);
      setTimeout(() => {
        handleClose();
      }, 5000);
    });
  };

  function isBetween7And30Days(dateString) {
    const inputDate = new Date(dateString);
    const today = new Date();
    const diffTime = today - inputDate;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    //console.log(diffDays)
    return diffDays >= 7 && diffDays <= 30;
  }

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Card className={classes.root} elevation={10}>
        <Fade in={showCheck} unmountOnExit>
          <div className={classes.hiddenContainer} style={{padding: 24}}>
            <Typography variant="h4" align="center" gutterBottom>
              {t('satisfactionQuote.thankYou')}
            </Typography>
            <CheckCircleIcon className={classes.checkIcon} />
          </div>
        </Fade>

        <Fade in={!rate} unmountOnExit>
          <CardActionArea>
            <CardContent className={classes.cardContent}>
              <Typography gutterBottom variant="h5" component="h2">
                {t('satisfactionQuote.title')}
              </Typography>
              <IconButton onClick={handleClose} color="default" size="medium" className={classes.closeBtn}>
                <CloseIcon />
              </IconButton>

              <div className={classes.ratingContainer}>
                <Rating
                  onChange={handleRatingChange}
                  name="sentiment-rating"
                  IconContainerComponent={IconContainer}
                  highlightSelectedOnly
                  max={5}
                  size="large"
                />
              </div>

              <Typography variant="body2" color="textSecondary" component="p">
                {t('satisfactionQuote.subline')}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Fade>

        <Fade in={showThankYou} unmountOnExit>
          <div className={classes.hiddenContainer}>
            <CardContent className={classes.cardContent}>
              <Button onClick={handleBack} variant="text" color="default" size="medium" className={classes.backBtn}>
                <ArrowBackIcon /> {t('satisfactionQuote.back')}
              </Button>
              <IconButton onClick={handleClose} color="default" size="medium" className={classes.closeBtn}>
                <CloseIcon />
              </IconButton>
              <div className={classes.innerCardContent}>
                <Typography variant="h4" align="center" gutterBottom>
                  {t(`satisfactionQuote.thanksTitle${rate}`)}
                </Typography>
                <Typography variant="body1">
                  {t(`satisfactionQuote.thanksDescription${rate}`)}
                </Typography>
              </div>
              <CardActions classes={{ root: classes.cardActions }}>
                <Button
                  size="large"
                  color="secondary"
                  variant="contained"
                  onClick={writeReview}
                  fullWidth
                >
                  {t('satisfactionQuote.reviewBtn')}
                </Button>
              </CardActions>
            </CardContent>
          </div>
        </Fade>

        <Fade in={showForm} unmountOnExit>
          <div className={classes.hiddenContainer}>
            <CardContent className={classes.cardContent}>
              <Button onClick={handleBack} variant="text" color="default" size="medium" className={classes.backBtn}>
                <ArrowBackIcon /> {t('satisfactionQuote.back')}
              </Button>
              <IconButton onClick={handleClose} color="default" size="medium" className={classes.closeBtn}>
                <CloseIcon />
              </IconButton>
              <div className={classes.innerCardContent}>
                <Typography variant="h4" align="center" gutterBottom>
                  {t(`satisfactionQuote.thanksTitle${rate}`)}
                </Typography>
                <Typography variant="body1">
                  {t(`satisfactionQuote.thanksDescription${rate}`)}
                </Typography>
                <div className={classes.feedbackForm}>
                  <TextField
                    label={t('satisfactionQuote.feedbackLabel')}
                    multiline
                    rows={3}
                    variant="outlined"
                    fullWidth
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    disabled={sendingFeedback}
                  />
                  <Button
                    size="large"
                    variant="contained"
                    color="secondary"
                    onClick={handleSubmit}
                    disabled={!feedback || sendingFeedback}
                    fullWidth
                  >
                    {sendingFeedback ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t('satisfactionQuote.submitBtn')
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </div>
        </Fade>
      </Card>
    </Slide>
  );
}